import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChatSession, ChatMessage, sendMessage, getChatDisplayMessages, getEvents, checkGenerationStatus } from '../services/tauri-api';
import { generateZeroZeroPath } from '../utils/generationUtils';
import { isTauriEnvironment } from '../services/api-client';
import { listenForGenerationStopped } from '../services/sse-client';
import ChatMessageComponent from './ChatMessage';

const NO_PROGRESS_LIMIT = 240; // 約6分（1.5s * 240）

interface ChatViewProps {
  chat: ChatSession;
  onAIMessageClick?: (promptSessionId: string) => void;
  onMessageSent?: () => void;
}

const ChatView: React.FC<ChatViewProps> = ({ chat, onAIMessageClick, onMessageSent }) => {
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [localMessages, setLocalMessages] = useState<ChatMessage[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastMessageCountRef = useRef<number>(0);
  const noProgressCountRef = useRef<number>(0);
  const currentSessionIdRef = useRef<string | null>(null);
  const lastEventTimestampRef = useRef<number>(0);
  const prevIsSendingRef = useRef<boolean>(false);

  // chatが変更されたら表示用メッセージを取得
  // チャットのmodel属性に基づいてタグ設定を事前に取得
  useEffect(() => {
    const loadDisplayMessages = async () => {
      console.log('chatプロップが更新されました。表示用メッセージを取得します:', chat.chat_id);
      
      // チャットのmodel属性に基づいてタグ設定を事前に取得（キャッシュに保存）
      if (chat.model) {
        const { getTagsConfigForModel } = await import('../utils/extractLastUserMessage');
        try {
          await getTagsConfigForModel(chat.model);
          console.log('モデルタグ設定を取得しました:', chat.model);
        } catch (error) {
          console.error('モデルタグ設定の取得に失敗:', error);
        }
      }
      
      const response = await getChatDisplayMessages(chat.chat_id);
      if (response.success && response.data) {
        console.log('表示用メッセージを取得:', response.data.length, '件');
        console.log('メッセージ詳細:', response.data);
        setLocalMessages(response.data);
        lastMessageCountRef.current = response.data.length;
        noProgressCountRef.current = 0;
      } else {
        console.error('表示用メッセージの取得に失敗:', response.error);
        setLocalMessages([]); // エラー時は空配列
        lastMessageCountRef.current = 0;
        noProgressCountRef.current = 0;
      }
    };
    loadDisplayMessages();
  }, [chat.chat_id, chat.model]); // chat_idとmodelが変更されたとき

  const clearPollingInterval = useCallback(() => {
    if (pollIntervalRef.current) {
      console.log('[ChatView] Clear generation polling');
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const handleGenerationComplete = useCallback(async () => {
    clearPollingInterval();
    setIsGenerating(false);
    setCurrentSessionId(null);
    currentSessionIdRef.current = null;
    noProgressCountRef.current = 0;
    console.log('[ChatView] handleGenerationComplete fired');
    
    // チャットのmodel属性に基づいてタグ設定を事前に取得（キャッシュに保存）
    if (chat.model) {
      const { getTagsConfigForModel } = await import('../utils/extractLastUserMessage');
      try {
        await getTagsConfigForModel(chat.model);
      } catch (error) {
        console.error('モデルタグ設定の取得に失敗:', error);
      }
    }
    
    const displayResponse = await getChatDisplayMessages(chat.chat_id);
    if (displayResponse.success && displayResponse.data) {
      setLocalMessages(displayResponse.data);
      lastMessageCountRef.current = displayResponse.data.length;
      // 生成完了時に親コンポーネントへ通知し、チャット一覧や現在のチャットを更新
      if (onMessageSent) {
        onMessageSent();
      }
    }
  }, [
    chat.chat_id,
    chat.model,
    clearPollingInterval,
    onMessageSent,
  ]);

  useEffect(() => {
    let isUnmounted = false;
    let eventSource: EventSource | null = null;
    let unlistenFn: (() => void) | null = null;

    if (isTauriEnvironment()) {
      // Tauri環境: Tauriのlistenを使用
      const { listen } = require('@tauri-apps/api/event');
      // requireでインポートした関数には型引数を使えないため、型引数を削除
      const unlistenPromise = (listen as any)('generation-stopped', async (event: { payload: string }) => {
        console.log('[ChatView] generation-stopped event received:', event.payload);
        if (isUnmounted) {
          console.log('[ChatView] component unmounted, ignoring event');
          return;
        }
        if (!currentSessionIdRef.current || currentSessionIdRef.current !== event.payload) {
          console.log('[ChatView] event does not match active session, ignore');
          return;
        }
        await handleGenerationComplete();
      });

      // unlisten関数を保存
      unlistenPromise.then((unlisten: () => void) => {
        if (typeof unlisten === 'function') {
          unlistenFn = unlisten;
        }
      }).catch((err: any) => {
        console.error('[ChatView] Failed to set up event listener:', err);
      });
    } else {
      // Web環境: SSEを使用（currentSessionIdが設定されている場合のみ）
      const sessionId = currentSessionIdRef.current;
      if (sessionId) {
        eventSource = listenForGenerationStopped(sessionId, async () => {
          if (isUnmounted) {
            return;
          }
          // セッションIDが一致する場合のみ処理
          if (currentSessionIdRef.current === sessionId) {
            await handleGenerationComplete();
          }
        });
      }
    }

    return () => {
      isUnmounted = true;
      if (eventSource) {
        eventSource.close();
      }
      if (unlistenFn) {
        try {
          unlistenFn();
        } catch (err) {
          console.error('[ChatView] Error unlistening to generation-stopped event:', err);
        }
      }
    };
  }, [handleGenerationComplete, currentSessionId]);

  useEffect(() => {
    return () => {
      clearPollingInterval();
    };
  }, [clearPollingInterval]);

  // isSending が true → false に切り替わったタイミングで親に通知し、チャット一覧などをリロード
  useEffect(() => {
    if (prevIsSendingRef.current && !isSending) {
      if (onMessageSent) {
        onMessageSent();
      }
    }
    prevIsSendingRef.current = isSending;
  }, [isSending, onMessageSent]);

  useEffect(() => {
    if (isGenerating && currentSessionId && !pollIntervalRef.current) {
      console.log('[ChatView] start polling for events (session:', currentSessionId, ')');
      lastEventTimestampRef.current = Date.now();
      
      pollIntervalRef.current = setInterval(async () => {
        if (!currentSessionIdRef.current || currentSessionIdRef.current !== currentSessionId) {
          return;
        }
        
        // イベントを取得（最後に取得したイベント以降）
        const eventsResponse = await getEvents(currentSessionId, lastEventTimestampRef.current);
        if (eventsResponse.success && eventsResponse.data) {
          const events = eventsResponse.data;
          
          for (const event of events) {
            const eventTimestamp = new Date(event.timestamp).getTime();
            if (eventTimestamp > lastEventTimestampRef.current) {
              lastEventTimestampRef.current = eventTimestamp;
            }
            
            console.log('[ChatView] Event received:', event.type, event);
            
            if (event.type === 'word_added') {
              // 単語が追加されたイベント - メッセージを更新
              noProgressCountRef.current = 0;
              const displayResponse = await getChatDisplayMessages(chat.chat_id);
              if (displayResponse.success && displayResponse.data) {
                setLocalMessages(displayResponse.data);
                lastMessageCountRef.current = displayResponse.data.length;
              }
            } else if (event.type === 'generation_completed') {
              // 生成完了イベント
              console.log('[ChatView] generation completed (detected by event)');
              noProgressCountRef.current = 0;
              void handleGenerationComplete();
              return;
            }
          }
        }
        
        // フォールバック: 生成完了状態を確認（イベントが取得できない場合）
        const statusResponse = await checkGenerationStatus(currentSessionId);
        if (statusResponse.success && statusResponse.data) {
          const status = statusResponse.data.status;
          
          // 生成完了またはエラーの場合
          if (statusResponse.data.isComplete || status === 'completed' || status === 'error') {
            console.log('[ChatView] generation completed (detected by status check):', status);
            noProgressCountRef.current = 0;
            void handleGenerationComplete();
            return;
          }
          
          // 継続実行は generateZeroZeroPath で自動的に行われるため、ここでは何もしない
          // フロントエンドで generateZeroZeroPath を呼び出しているため、ポーリングは状態確認のみ
        }

        // タイムアウトチェック（フォールバック）
        if (noProgressCountRef.current >= NO_PROGRESS_LIMIT) {
          console.log('[ChatView] generation timeout fired (no progress)');
          noProgressCountRef.current = 0;
          void handleGenerationComplete();
        }
      }, 500); // 500ms間隔でポーリング（イベントベースの更新）
    }

    if (!isGenerating) {
      clearPollingInterval();
    }

    return () => {
      clearPollingInterval();
    };
  }, [
    isGenerating,
    chat.chat_id,
    onMessageSent,
    clearPollingInterval,
    currentSessionId,
    handleGenerationComplete,
  ]);

  // メッセージが更新されたらスクロール
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [localMessages]);

  const handleSendMessage = async (e?: React.MouseEvent | React.KeyboardEvent) => {
    // イベントの伝播を防ぐ
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const content = inputValue.trim();
    if (!content || isSending || isGenerating) return;

    try {
      setIsSending(true);
      
      // メッセージを送信してセッションを作成
      const sendResult = await sendMessage(chat.chat_id, content);
      if (!sendResult.success || !sendResult.data) {
        console.error('[ChatView] Failed to send message:', sendResult.error);
        setIsSending(false);
        setIsGenerating(false);
        return;
      }

      const sessionId = sendResult.data.prompt_session_id;
      if (!sessionId) {
        console.error('[ChatView] No session ID returned from sendMessage');
        setIsSending(false);
        setIsGenerating(false);
        return;
      }

      setCurrentSessionId(sessionId);
      currentSessionIdRef.current = sessionId;
      setIsGenerating(true);
      setInputValue('');
      noProgressCountRef.current = 0;

      // メッセージを再読み込み
      const displayResponse = await getChatDisplayMessages(chat.chat_id);
      if (displayResponse.success && displayResponse.data) {
        setLocalMessages(displayResponse.data);
        lastMessageCountRef.current = displayResponse.data.length;
      }

      // 0.0パスを進んで自動生成を開始（完了まで待機）
      try {
        const result = await generateZeroZeroPath(sessionId, 60, (step, currentNodeId) => {
          console.log(`[ChatView] Generation progress: step ${step}, nodeId: ${currentNodeId}`);
          // 進捗に応じてメッセージを更新（オプション）
          // generateZeroZeroPath の各ステップごとに最新メッセージを取得して逐次反映する
          getChatDisplayMessages(chat.chat_id).then(response => {
            if (response.success && response.data) {
              setLocalMessages(response.data);
            }
          });
        });
        
        console.log('[ChatView] Generation completed:', result);
        
        // 最終的なメッセージを取得
        const displayResponse = await getChatDisplayMessages(chat.chat_id);
        if (displayResponse.success && displayResponse.data) {
          setLocalMessages(displayResponse.data);
          lastMessageCountRef.current = displayResponse.data.length;
        }
      } catch (error) {
        console.error('[ChatView] Generation error:', error);
      } finally {
        // 生成が完了したら、生成中フラグを解除
        setIsGenerating(false);
        setCurrentSessionId(null);
        currentSessionIdRef.current = null;
        noProgressCountRef.current = 0;
        setIsSending(false);
      }
    } catch (error) {
      console.error('メッセージ送信エラー:', error);
      setIsGenerating(false);
      setCurrentSessionId(null);
      currentSessionIdRef.current = null;
      setInputValue('');
      
      // エラー時もメッセージを再読み込み
      const displayResponse = await getChatDisplayMessages(chat.chat_id);
      if (displayResponse.success && displayResponse.data) {
        setLocalMessages(displayResponse.data);
        lastMessageCountRef.current = displayResponse.data.length;
        noProgressCountRef.current = 0;
      }
    } finally {
      setIsSending(false);
      // isGenerating は生成完了イベント/フォールバックでOFFにする（途中で連打できないようにする）
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      handleSendMessage(e);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* メッセージ一覧 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {localMessages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <p>メッセージがありません。最初のメッセージを送信してください。</p>
          </div>
        ) : (
          <>
            {localMessages.map((message, index) => {
              const isLast = index === localMessages.length - 1;
              // 生成中または送信中で、最後のメッセージがアシスタントの場合、スピナーを表示
              // 送信ボタンと同じフラグ（isSending || isGenerating）を参照
              const showSpinner =
                isLast && 
                (isSending || isGenerating) && 
                message.role === 'assistant';
              return (
                <ChatMessageComponent
                  key={message.id}
                  message={message}
                  onAIMessageClick={onAIMessageClick}
                  isGenerating={isGenerating}
                  isSending={isSending}
                  showGeneratingSpinner={showSpinner}
                  modelName={chat.model}
                />
              );
            })}
            {/* 生成中または送信中で最後のメッセージがユーザーメッセージの場合、プレースホルダーのAIメッセージを表示 */}
            {(isSending || isGenerating) && 
             localMessages.length > 0 && 
             localMessages[localMessages.length - 1].role === 'human' && 
             !!currentSessionId && (
              <ChatMessageComponent
                key="generating-placeholder"
                message={{
                  id: `msg_generating_${currentSessionId}`,
                  role: 'assistant',
                  content: '',
                  prompt_session_id: currentSessionId,
                  timestamp: new Date().toISOString(),
                }}
                onAIMessageClick={onAIMessageClick}
                isGenerating={isGenerating}
                isSending={isSending}
                showGeneratingSpinner={true}
                modelName={chat.model}
              />
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* メッセージ入力欄 */}
      <div className="border-t border-gray-200 p-4 bg-gray-50">
        <div className="flex items-end gap-2">
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="メッセージを入力..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            rows={1}
            disabled={isSending || isGenerating}
            style={{ minHeight: '40px', maxHeight: '120px' }}
          />
          <button
            onClick={(e) => handleSendMessage(e)}
            disabled={!inputValue.trim() || isSending || isGenerating}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {isSending || isGenerating ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>送信中...</span>
              </div>
            ) : (
              '送信'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatView;

